import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SpriteIcon } from '@/components/SpriteIcon';

/**
 * Transient notices, stacked bottom-right and gone on their own.
 *
 * This is the other half of what `window.alert` was doing. An outcome the user
 * asked for and got — the access email went out — is not worth a modal and a
 * click: it interrupts a queue of admin actions to say that nothing went wrong.
 * Failures stay with `useAlert`, where the dialog blocks until acknowledged,
 * because those the user must actually read.
 *
 *     const toast = useToast();
 *     toast({ variant: 'success', message: t('Users.AccessEmailSent', …) });
 *
 * Several at once stack rather than replace each other, so a run of actions
 * reports each one.
 */

export type ToastVariant = 'success' | 'info' | 'primary' | 'warning' | 'danger';

/** Glyph shown when the caller does not name one, per variant. */
const DEFAULT_ICON: Record<ToastVariant, string> = {
  success: 'check-circle',
  info: 'info',
  primary: 'info',
  warning: 'alert-triangle',
  danger: 'alert-circle',
};

const DEFAULT_DELAY_MS = 5000;

export interface ToastOptions {
  message: ReactNode;
  title?: ReactNode;
  /** Colours the glyph. Defaults to `success` — the case this exists for. */
  variant?: ToastVariant;
  /** Name of a glyph in the shared sprite; overrides the variant's default. */
  icon?: string;
  /** Milliseconds on screen. 0 keeps it until the user closes it. */
  delay?: number;
}

export type ToastFn = (options: ToastOptions) => void;

const ToastContext = createContext<ToastFn | null>(null);

interface ActiveToast extends ToastOptions {
  id: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback<ToastFn>((options) => {
    // The id is taken here rather than inside the updater: React may run an
    // updater twice, and it must stay a pure function of its argument.
    const id = nextId.current++;
    setToasts((current) => [...current, { ...options, id }]);
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* `containerPosition` rather than a position-fixed class: without it the
          container is absolute and would scroll away with the page. */}
      <ToastContainer position="bottom-end" containerPosition="fixed" className="p-3">
        {toasts.map((toast) => {
          const variant = toast.variant ?? 'success';
          const delay = toast.delay ?? DEFAULT_DELAY_MS;

          return (
            <Toast
              key={toast.id}
              onClose={() => dismiss(toast.id)}
              delay={delay === 0 ? undefined : delay}
              autohide={delay !== 0}
              // A success notice must not interrupt a screen reader mid-sentence;
              // anything that went wrong should.
              role={variant === 'danger' ? 'alert' : 'status'}
              aria-live={variant === 'danger' ? 'assertive' : 'polite'}
            >
              <Toast.Body className="d-flex align-items-start gap-2">
                <span className={`text-${variant}-emphasis`}>
                  <SpriteIcon
                    name={toast.icon ?? DEFAULT_ICON[variant]}
                    size={18}
                    className="sa-icon sa-medium sa-current sa-nofill"
                  />
                </span>
                <div className="flex-grow-1">
                  {toast.title && <div className="fw-semibold">{toast.title}</div>}
                  <div>{toast.message}</div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dismiss(toast.id)}
                  aria-label={t('Button.Close', 'Close')}
                />
              </Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastFn {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast requires a <ToastProvider> above it.');
  return toast;
}
